<template>
  <div
    class="form-card"
    v-bind="$attrs"
    :class="{
      'is-open': props.isOpen,
      'has-error': props.hasError,
      'has-background-image': !!props.backgroundImageUrl,
    }"
    :data-card-animating="isAnimating"
    @click="!props.isOpen && handleCardClick()"
    ref="cardRef">
    <div v-if="!props.isOpen && props.backgroundImageUrl && !isAnimating" class="card-background-wrapper">
      <img :src="props.backgroundImageUrl" class="card-background-image" alt="" />
      <div class="card-background-gradient"></div>
    </div>
    <div class="card-header cursor-pointer">
      <template v-if="showCollapsedSummary">
        <IconCircleXFilled v-if="props.hasError" size="20" class="icon-check error" stroke-width="1.5" />
        <IconCircleCheckFilled
          v-else
          size="20"
          class="icon-check"
          stroke-width="1.5"
          :class="{ completed: props.completed }" />
      </template>
      <div class="form-card-content">
        <div v-if="showCollapsedSummary" class="form-card-description">
          <div class="form-card-title">
            <slot name="title">
              {{ props?.title }}
            </slot>
          </div>
          <slot name="description">
            <div v-if="props.description" class="summary-title">{{ props.description }}</div>
            <div v-if="props.subDescription" class="summary-subtitle">{{ props.subDescription }}</div>
          </slot>
        </div>
      </div>
      <slot v-if="showCollapsedSummary" name="actions"> </slot>
    </div>
    <div class="header-chevron" v-if="showCollapsedSummary && props.showChevron">
      <IconSquareChevronDownFilled size="24" class="text-muted" color="#0000001A" />
    </div>

    <transition
      name="card-body-fade"
      @before-enter="onBodyBeforeEnter"
      @enter="onBodyEnter"
      @after-enter="onBodyAfterEnter"
      @before-leave="onBodyBeforeLeave"
      @leave="onBodyLeave"
      @after-leave="onBodyAfterLeave">
      <div v-if="props.isOpen" class="card-body" @click.stop>
        <slot name="pre-form-content"></slot>
        <div class="card-header cursor-pointer" @click="handleCardClick">
          <template v-if="props.isOpen && props.showFormIconsOnOpen">
            <IconCircleXFilled v-if="props.hasError" size="20" class="icon-check error" stroke-width="1.5" />
            <IconCircleCheckFilled
              v-else
              size="20"
              class="icon-check"
              stroke-width="1.5"
              :class="{ completed: props.completed }" />
          </template>
          <div class="form-card-title" style="margin-bottom: 20px">
            <slot name="title">
              {{ props?.title }}
            </slot>
          </div>
        </div>
        <slot name="form-content"> </slot>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { IconCircleCheckFilled, IconCircleXFilled, IconSquareChevronDownFilled } from '@tabler/icons-vue';

const isAnimating = ref(false);
const cardRef = ref<HTMLElement | null>(null);

// Height the body should start at on open and end at on leave so the card
// height is visually continuous across the summary⇄body swap. Equal to the
// collapsed summary's content height (card height minus vertical padding),
// measured when the summary is rendered. 0 is a safe fallback — it just
// falls back to the plain 0-height animation we had before the anchor.
let bodyAnchorHeight = 0;

function captureBodyAnchorHeight() {
  const el = cardRef.value;
  if (!el) return;
  const style = getComputedStyle(el);
  const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  bodyAnchorHeight = Math.max(0, el.offsetHeight - padding);
}

// Hide the collapsed summary (icons, title/description, chevron, actions) while
// the body is animating — otherwise on close the summary renders simultaneously
// with the still-leaving body and the card height briefly jumps *up* before
// shrinking. Rendering the summary only once animation settles keeps the
// transition monotonic.
const showCollapsedSummary = computed(() => !props.isOpen && !isAnimating.value);

const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;
    summary?: string;
    subDescription?: string;
    completed?: boolean;
    isOpen?: boolean;
    hasError?: boolean;
    showChevron?: boolean;
    backgroundImageUrl?: string | null;
    showFormIconsOnOpen?: boolean;
  }>(),
  {
    completed: false,
    isOpen: false,
    hasError: false,
    showChevron: false,
    backgroundImageUrl: null,
    showFormIconsOnOpen: true,
  }
);

const emit = defineEmits<{
  toggle: [title: string];
  enterPressed: [];
}>();

const handleCardClick = () => {
  emit('toggle', props.title || '');
};

// Animate height alongside the opacity/transform fade so the card grows and
// shrinks smoothly instead of snapping. Measuring scrollHeight keeps the
// animation in sync with the actual body content. The active `transition: all`
// from the .card-body-fade-*-active classes drives the timing for height too,
// so we only need to flip the inline height values.
//
// The start (on enter) and end (on leave) height is bodyAnchorHeight — the
// collapsed summary's content height. That keeps the card visually the same
// size across the summary→body swap, so the user doesn't see a dip at the
// start of open or a pop at the end of close.
function onBodyBeforeEnter(el: Element) {
  isAnimating.value = true;
  const e = el as HTMLElement;
  e.style.height = `${bodyAnchorHeight}px`;
  e.style.overflow = 'hidden';
}
function onBodyEnter(el: Element, done: () => void) {
  const e = el as HTMLElement;
  e.style.height = `${e.scrollHeight}px`;
  const onEnd = (ev: TransitionEvent) => {
    if (ev.target !== e || ev.propertyName !== 'height') return;
    e.removeEventListener('transitionend', onEnd as EventListener);
    done();
  };
  e.addEventListener('transitionend', onEnd as EventListener);
}
function onBodyAfterEnter(el: Element) {
  const e = el as HTMLElement;
  e.style.height = '';
  e.style.overflow = '';
  isAnimating.value = false;
}
function onBodyBeforeLeave(el: Element) {
  isAnimating.value = true;
  const e = el as HTMLElement;
  e.style.height = `${e.scrollHeight}px`;
  e.style.overflow = 'hidden';
  // Force reflow so the explicit height is committed before the leave class
  // flips it to the anchor — otherwise the browser skips to the end state.
  void e.offsetHeight;
}
function onBodyLeave(el: Element, done: () => void) {
  const e = el as HTMLElement;
  e.style.height = `${bodyAnchorHeight}px`;
  const onEnd = (ev: TransitionEvent) => {
    if (ev.target !== e || ev.propertyName !== 'height') return;
    e.removeEventListener('transitionend', onEnd as EventListener);
    done();
  };
  e.addEventListener('transitionend', onEnd as EventListener);
}
function onBodyAfterLeave() {
  isAnimating.value = false;
  // Summary is about to re-render — refresh the anchor from the real DOM so
  // the next open/close starts from the current summary height.
  nextTick(captureBodyAnchorHeight);
}

// Capture the collapsed summary's height right before Vue commits the DOM
// change, so we have it available for the enter hook (where the summary has
// already unmounted).
watch(
  () => props.isOpen,
  (nv, ov) => {
    if (ov === false && nv === true) captureBodyAnchorHeight();
  },
  { flush: 'sync' }
);

function handleEnterKey(event: KeyboardEvent) {
  if (event.key !== 'Enter') return;
  if (!props.isOpen) return;

  const target = event.target as HTMLElement;
  if (target.tagName === 'TEXTAREA' || target.isContentEditable) return;

  if (event.defaultPrevented) return;

  event.preventDefault();
  event.stopPropagation();
  emit('enterPressed');
}

onMounted(() => {
  window.addEventListener('keydown', handleEnterKey);
  // Prime the anchor for cards that start collapsed so the very first open
  // animates smoothly without a dip.
  if (!props.isOpen) nextTick(captureBodyAnchorHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEnterKey);
});
</script>

<style scoped lang="scss">
// Transition for card body only - smoother easing
.card-body-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.card-body-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

.card-body-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.card-body-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

.card-body-fade-enter-to,
.card-body-fade-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.form-card {
  padding: 15px 20px;
  border-radius: 5px;
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  transition: all 0.2s ease-in-out;
  position: relative;
  z-index: 1;

  &.is-open {
    z-index: 10;
  }

  &.has-error {
    border-color: #d43232;
  }
}

.card-background-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  overflow: hidden;
  z-index: 0;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  position: relative;
  z-index: 2;
  user-select: none;

  &:hover {
    border-radius: 4px;
    margin: -4px;
    padding: 4px;
  }
}

.icon-check {
  color: #eaecf0;
  margin-top: 2px;
  transition: color 0.2s ease-in-out;
  flex-shrink: 0;

  &.completed {
    color: var(--tblr-primary);
  }

  &.error {
    color: #d43232;
  }
}

.form-card-content {
  margin-right: auto;
  // Without these, wide content in the description slot (e.g. long breadcrumbs)
  // would push the card past its parent's width.
  min-width: 0;
  max-width: 100%;
  flex: 1 1 auto;

  .form-card-description {
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: #101828;
    transition: color 0.2s ease-in-out;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    min-width: 0;
  }
}

.form-card-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: #101828;
  transition: color 0.2s ease-in-out;
  margin-bottom: 10px;
}

.header-chevron {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.summary-title {
  font-weight: 400;
  font-size: 14px;
  color: #101828;
}

.summary-subtitle {
  font-weight: 400;
  font-size: 13px;
  color: #8e939d;
}

.card-background-image {
  position: absolute;
  top: 0;
  left: 40%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  border-radius: 8px;
  object-position: right;
  transform: scale(1.8);
}

.card-background-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(280.85deg, rgba(255, 255, 255, 0) 20%, #ffffff 50%);
  z-index: 1;
  border-radius: 8px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
